// BotStructuredMessage.tsx
// Renders a bot reply as a data table when listData (action.data.columns +
// action.data.table_rows) is provided, otherwise falls back to plain text.
//
// Table headers → column.label  for each column in order
// Table cells   → column.key used to look up the value in each table_row
// "#" column    → the "position" column (key="position", label="#") from the API
//                 carries the row number — no extra hardcoded index column needed.

import React, {useMemo} from 'react';
import {View, Text, ScrollView} from 'react-native';
import {
  BotStructuredMessageProps,
  DataTableProps,
  ListData,
  TableData,
} from '../../../shared/components/NovaAI/NovaAIProps';
import {VISIBLE_ROWS} from '../../../shared/components/NovaAI/constants/novaAI.constant';
import {useIsZoomed} from '../../hooks/useSiteHeaderHeight';
import {ZoomedHorizontalScroll} from '../ZoomedHorizontalScroll';

// ─── Table builder from API list_data ────────────────────────────────────────

/**
 * Builds a TableData directly from the API's structured list data.
 *
 * - colHeaders : column.label for every column in order
 * - dataRows   : for each table_row, column.key is used to look up the cell value
 *
 * The column with key "position" (label "#") is included as a normal column
 * and carries the row number (1, 2, 3 …) — no separate index column is needed.
 */
function buildTableFromListData(message: string, listData: ListData): TableData {
  const colHeaders = listData.columns.map(col => col.label);
  const dataRows = listData.table_rows.map(row =>
    listData.columns.map(col => String(row[col.key] ?? '—')),
  );
  return {header: message, colHeaders, dataRows};
}

// ─── Shared table renderer ────────────────────────────────────────────────────

function DataTable({table, styles}: DataTableProps) {
  const {header, colHeaders, dataRows} = table;
  const showScrollHint = dataRows.length > VISIBLE_ROWS;
  const isZoomed = useIsZoomed();

  // ── Column header row ─────────────────────────────────────────────────────
  const renderHeader = () => {
    if (colHeaders.length === 0) return null;
    return (
      <View style={styles.tableColHeaderRow}>
        {colHeaders.map((col, i) => (
          <View
            key={i}
            style={[
              styles.tableCell,
              i === colHeaders.length - 1 && styles.tableLastCell,
            ]}>
            <Text style={styles.tableColHeaderCellText} numberOfLines={2}>
              {col}
            </Text>
          </View>
        ))}
      </View>
    );
  };

  // ── Single data row ───────────────────────────────────────────────────────
  const renderRow = (cols: string[], rowIdx: number) => (
    <View
      key={rowIdx}
      style={styles.tableRow}>
      {cols.map((col, colIdx) => (
        <View
          key={colIdx}
          style={[
            styles.tableCell,
            colIdx === cols.length - 1 && styles.tableLastCell,
          ]}>
          <Text style={styles.tableCellText} numberOfLines={3}>
            {col}
          </Text>
        </View>
      ))}
    </View>
  );

  return (
    <View style={styles.tableWrapper}>
      {header ? (
        <Text style={styles.tableHeaderText}>{header}</Text>
      ) : null}

      {/* At extreme zoom the bubble is only ~220 px wide, so the table scrolls
          sideways inside it (each column keeps a readable width) instead of
          breaking every word mid-letter. */}
      <ZoomedHorizontalScroll
        enabled={isZoomed}
        minWidth={Math.max(260, colHeaders.length * 96)}>
      <View style={styles.tableContainer}>
        {/* ── Column header row ── */}
        {renderHeader()}

        {/* ── Data rows — ScrollView works correctly when nested inside the
              outer chat FlatList; a FlatList-inside-FlatList (same axis)
              is blocked by React Native's VirtualizedList nesting guard. ── */}
        <ScrollView
          style={styles.tableScrollArea}
          nestedScrollEnabled
          showsVerticalScrollIndicator>
          {dataRows.map(renderRow)}
        </ScrollView>

        {/* ── Scroll hint ── */}
        {/* {showScrollHint && (
          <View style={styles.tableScrollHint}>
            <Text style={styles.tableScrollHintText}>
              Scroll to see {dataRows.length - VISIBLE_ROWS} more{' '}
              {dataRows.length - VISIBLE_ROWS === 1 ? 'item' : 'items'}
            </Text>
          </View>
        )} */}
      </View>
      </ZoomedHorizontalScroll>
    </View>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

const BotStructuredMessage: React.FC<BotStructuredMessageProps> = ({
  text,
  listData,
  styles,
}) => {
  // Split the reply at \n\n so the format is always:
  //   [main message]   ← used as table header or first text line
  //   [table / "No result found"]
  //   [follow-up question, e.g. "Would you like me to open the repairs screen?"]
  const parts = text ? text.split('\n\n') : [''];
  const mainText: string = (parts[0] ?? '').trim();
  const followUpText: string = parts.slice(1).join('\n\n').trim();

  const table = useMemo<TableData | null>(() => {
    if (listData?.columns?.length && listData?.table_rows?.length) {
      // Pass only the main (first) part as the table header
      return buildTableFromListData(mainText.trim(), listData);
    }
    return null;
  }, [text, listData]);

  // Has column headers defined but no data rows → show "No result found"
  if (listData?.columns?.length && !listData?.table_rows?.length) {
    return (
      <View>
        {!!mainText.trim() && (
          <Text style={styles.botMessageText}>{mainText.trim()}</Text>
        )}
        <Text style={styles.noResultText}>No result found</Text>
        {!!followUpText && (
          <Text style={[styles.botMessageText, {marginTop: 4}]}>
            {followUpText}
          </Text>
        )}
      </View>
    );
  }

  // No structured list data → render as plain text
  if (!table || table.colHeaders.length === 0) {
    return <Text style={styles.botMessageText}>{text}</Text>;
  }

  // Has table data → render table then follow-up text below
  return (
    <View>
      <DataTable table={table} styles={styles} />
      {!!followUpText && (
        <Text style={[styles.botMessageText, {marginTop: 6}]}>
          {followUpText}
        </Text>
      )}
    </View>
  );
};

export default BotStructuredMessage;